import React from 'react';
import SEO from '../components/SEO';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield, AlertTriangle, FileText, Users, Wrench } from 'lucide-react';

const TermsPage = () => {
  const currentDate = new Date().toLocaleDateString('en-US', { 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  });

  return (
    <main data-testid="terms-page" className="bg-gray-50 min-h-screen">
      <SEO 
        title="Terms of Service | BBM Appliances"
        description="Terms of Service for BBM Appliances repair services. Read our service agreement, liability limitations, and subcontractor policies."
        canonical="https://www.bbmappliances.com/terms"
      />
      
      {/* Header */}
      <div className="bg-foreground text-white py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link 
            to="/" 
            data-testid="terms-back-link"
            className="inline-flex items-center gap-2 text-white/70 hover:text-white mb-6 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
          <h1 className="text-3xl sm:text-4xl font-heading font-bold">Terms of Service</h1>
          <p className="text-white/70 mt-4">Last Updated: {currentDate}</p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-2xl shadow-sm p-8 sm:p-12 space-y-10">
          
          {/* Introduction */}
          <section data-testid="terms-intro">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                <FileText className="w-5 h-5 text-primary" />
              </div>
              <h2 className="text-xl font-heading font-semibold">Agreement to Terms</h2>
            </div>
            <p className="text-gray-600 leading-relaxed">
              By scheduling, booking, or using any services provided by BBM Appliances (DBA), you agree to be bound by these Terms of Service. 
              Please read these terms carefully before using our services. If you do not agree with any part of these terms, 
              you may not use our services.
            </p>
          </section>

          {/* Service Provider Information */}
          <section data-testid="terms-subcontractor">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                <Users className="w-5 h-5 text-primary" />
              </div>
              <h2 className="text-xl font-heading font-semibold">Service Providers & Subcontractors</h2>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 mb-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <p className="text-amber-800 text-sm leading-relaxed">
                  <strong>Important Notice:</strong> BBM Appliances may utilize independent subcontractors, 
                  technicians, or third-party service providers to perform repair and maintenance services.
                </p>
              </div>
            </div>
            <div className="text-gray-600 leading-relaxed space-y-4">
              <p>
                By using our services, you acknowledge and agree to the following:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>
                  Repair services may be performed by independent subcontractors or technicians operating under their own business licenses and insurance.
                </li>
                <li>
                  Each subcontractor or technician is <strong>fully responsible</strong> for their own workmanship and any damage that may occur <strong>during</strong> the active performance of the repair service.
                </li>
                <li>
                  BBM Appliances acts as a booking and coordination service connecting customers with qualified service providers.
                </li>
                <li>
                  All service providers are vetted for qualifications but operate as independent entities.
                </li>
              </ul>
            </div>
          </section>

          {/* Liability Limitations */}
          <section data-testid="terms-liability">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                <Shield className="w-5 h-5 text-red-600" />
              </div>
              <h2 className="text-xl font-heading font-semibold">Limitation of Liability</h2>
            </div>
            <div className="bg-red-50 border border-red-200 rounded-xl p-6 mb-4">
              <p className="text-red-800 text-sm font-medium">
                PLEASE READ THIS SECTION CAREFULLY AS IT LIMITS OUR LIABILITY
              </p>
            </div>
            <div className="text-gray-600 leading-relaxed space-y-4">
              <p>
                <strong>BBM Appliances and its affiliates shall NOT be held responsible or liable for:</strong>
              </p>
              <ul className="list-disc list-inside space-y-3 ml-4">
                <li>
                  <strong>Post-Service Issues:</strong> Any damage, scratches, dents, leaks, malfunctions, or issues of any kind that occur <strong>after</strong> the technician or subcontractor has completed the service and left the customer's premises.
                </li>
                <li>
                  <strong>Unrelated Problems:</strong> Any issues, failures, or damages to parts of the appliance that were <strong>not specifically addressed</strong> or repaired during the service call.
                </li>
                <li>
                  <strong>Pre-existing Conditions:</strong> Any pre-existing damage, wear, corrosion, or deterioration that existed before our service was performed.
                </li>
                <li>
                  <strong>Used or Older Appliances:</strong> Unforeseen issues, leaks, or failures on used, refurbished, or older appliances. Due to the age and condition of such units, we cannot predict or be responsible for problems that may develop after service completion.
                </li>
                <li>
                  <strong>Consequential Damages:</strong> Any indirect, incidental, special, or consequential damages including but not limited to water damage, property damage, food spoilage, loss of use, or business interruption.
                </li>
                <li>
                  <strong>Third-Party Actions:</strong> Any damage caused by other service providers, the customer, or third parties after our service is complete.
                </li>
              </ul>
            </div>
          </section>

          {/* Service Scope */}
          <section data-testid="terms-scope">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                <Wrench className="w-5 h-5 text-primary" />
              </div>
              <h2 className="text-xl font-heading font-semibold">Scope of Service & Warranty</h2>
            </div>
            <div className="text-gray-600 leading-relaxed space-y-4">
              <p>
                <strong>Our service warranty and guarantee applies ONLY to:</strong>
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>The specific repair or service that was performed and documented</li>
                <li>Parts that were replaced or installed during the service call</li>
                <li>Issues that are directly related to the work performed</li>
              </ul>
              <p className="mt-4">
                <strong>Our warranty does NOT cover:</strong>
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>The entire appliance or components not serviced</li>
                <li>Normal wear and tear</li>
                <li>Damage from misuse, abuse, or improper operation</li>
                <li>Issues caused by power surges, water damage, or environmental factors</li>
                <li>Problems with parts not supplied or installed by our technicians</li>
              </ul>
            </div>
          </section>

          {/* Customer Responsibilities */}
          <section data-testid="terms-customer">
            <h2 className="text-xl font-heading font-semibold mb-4">Customer Responsibilities</h2>
            <div className="text-gray-600 leading-relaxed space-y-4">
              <p>By using our services, you agree to:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Provide accurate information about the appliance and the issue</li>
                <li>Ensure safe and clear access to the appliance for the technician</li>
                <li>Disclose any known pre-existing issues or previous repairs</li>
                <li>Be present or have an authorized adult present during the service</li>
                <li>Inspect the completed work before the technician leaves</li>
                <li>Report any concerns immediately upon service completion</li>
              </ul>
            </div>
          </section>

          {/* Payment Terms */}
          <section data-testid="terms-payment">
            <h2 className="text-xl font-heading font-semibold mb-4">Payment Terms</h2>
            <div className="text-gray-600 leading-relaxed space-y-4">
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Payment is due upon completion of service unless otherwise arranged</li>
                <li>We accept major credit cards, debit cards, and cash</li>
                <li>Service fees are non-refundable once the service has been performed</li>
                <li>Estimates provided are subject to change based on actual findings during repair</li>
              </ul>
            </div>
          </section>

          {/* Cancellation Policy */}
          <section data-testid="terms-cancellation">
            <h2 className="text-xl font-heading font-semibold mb-4">Cancellation Policy</h2>
            <div className="text-gray-600 leading-relaxed space-y-4">
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Cancellations must be made at least 24 hours before the scheduled appointment</li>
                <li>Late cancellations or no-shows may be subject to a cancellation fee</li>
                <li>We reserve the right to cancel or reschedule appointments due to unforeseen circumstances</li>
              </ul>
            </div>
          </section>

          {/* Dispute Resolution */}
          <section data-testid="terms-disputes">
            <h2 className="text-xl font-heading font-semibold mb-4">Dispute Resolution</h2>
            <div className="text-gray-600 leading-relaxed space-y-4">
              <p>
                Any disputes arising from or relating to these terms or our services shall be resolved through:
              </p>
              <ol className="list-decimal list-inside space-y-2 ml-4">
                <li>Direct communication with our customer service team</li>
                <li>Mediation, if direct resolution is not possible</li>
                <li>Binding arbitration in accordance with applicable state laws</li>
              </ol>
            </div>
          </section>

          {/* Changes to Terms */}
          <section data-testid="terms-changes">
            <h2 className="text-xl font-heading font-semibold mb-4">Changes to Terms</h2>
            <p className="text-gray-600 leading-relaxed">
              BBM Appliances reserves the right to modify these Terms of Service at any time. 
              Changes will be effective immediately upon posting to our website. Your continued use 
              of our services after any changes indicates your acceptance of the modified terms.
            </p>
          </section>

          {/* Contact Information */}
          <section data-testid="terms-contact" className="border-t pt-8">
            <h2 className="text-xl font-heading font-semibold mb-4">Contact Us</h2>
            <p className="text-gray-600 leading-relaxed mb-4">
              If you have any questions about these Terms of Service, please contact us:
            </p>
            <div className="bg-gray-50 rounded-xl p-6">
              <p className="font-medium text-gray-800">BBM Appliances</p>
              <p className="text-gray-600">Email: Hello@bbmappliances.com</p>
              <p className="text-gray-600">Phone: (855) 605-6034</p>
            </div>
          </section>

          {/* Acknowledgment */}
          <section data-testid="terms-acknowledgment" className="bg-primary/5 rounded-xl p-6">
            <p className="text-gray-700 text-sm leading-relaxed">
              <strong>By booking or using any services from BBM Appliances, you acknowledge that you have read, 
              understood, and agree to be bound by these Terms of Service.</strong> If you do not agree to these terms, 
              please do not use our services.
            </p>
          </section>

        </div>
      </div>
    </main>
  );
};

export default TermsPage;
